/** Legislation the compliance agent identified by reading the contract.
 *
 *  Distinct from the seeded matrix shown above it: this reaches any
 *  jurisdiction and any instrument, including a governing-law clause pointing
 *  somewhere the matrix has never heard of. The trade is that a model can cite
 *  a statute that does not exist, so every row carries whether the matrix
 *  independently agrees — and a contradiction is called out loudly.
 */
import { Badge } from '@/components/ui/badge';
import { BookOpen, Check, TriangleAlert, HelpCircle, ShieldAlert, ShieldCheck } from 'lucide-react';

import { isBreached, type AiLaw } from './lawCompliance';

export type { AiLaw };

const STATUS_META: Record<string, { label: string; cls: string; Icon: typeof Check }> = {
  corroborated: {
    label: 'Matches reference table',
    cls: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
    Icon: Check,
  },
  contradicted: {
    label: 'Disagrees with reference table',
    cls: 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400',
    Icon: TriangleAlert,
  },
  ai_only: {
    label: 'Not in reference table',
    cls: 'border-border bg-muted text-muted-foreground',
    Icon: HelpCircle,
  },
};

// Only the two decided verdicts get a badge. "unclear" renders nothing: a badge
// reading "not assessed" on every law would drown the ones that were — hence
// Partial, so an unrecognised status is undefined rather than silently indexed.
const COMPLIANCE_META: Partial<
  Record<string, { label: string; cls: string; Icon: typeof Check }>
> = {
  non_compliant: {
    label: 'Does not comply',
    cls: 'border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-400',
    Icon: ShieldAlert,
  },
  compliant: {
    label: 'Complies',
    cls: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
    Icon: ShieldCheck,
  },
};

interface AiLawListProps {
  laws: AiLaw[] | undefined;
  /**
   * Whether these laws have been cross-checked against the reference table.
   * False on the Compliance stage tab, which shows the agent's raw output
   * before the final gate corroborates it — claiming "not in reference table"
   * there would report an absent comparison as a negative result.
   */
  corroborated?: boolean;
}

export function AiLawList({ laws, corroborated = true }: AiLawListProps) {
  if (!laws || laws.length === 0) return null;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <BookOpen className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        <span className="text-sm font-medium">Identified from the document</span>
      </div>

      <ul className="space-y-2">
        {laws.map((law, i) => {
          const meta = STATUS_META[law.status ?? ''] ?? STATUS_META.ai_only;
          const { Icon } = meta;
          const compliance = COMPLIANCE_META[law.compliance_status ?? ''];
          const breached = isBreached(law);
          return (
            <li
              key={`${law.instrument ?? ''}-${i}`}
              className={`rounded border p-2.5 space-y-1 ${breached ? 'border-red-500/40' : ''}`}
            >
              <div className="flex flex-wrap items-center gap-2">
                {law.jurisdiction && (
                  <Badge variant="outline" className="text-[10px]">{law.jurisdiction}</Badge>
                )}
                <span className="text-sm font-medium">
                  {law.instrument}
                  {law.article_ref && (
                    <span className="font-normal text-muted-foreground">, {law.article_ref}</span>
                  )}
                </span>
                {compliance && (
                  <Badge
                    variant="outline"
                    className={`inline-flex items-center gap-1 text-[10px] ${compliance.cls}`}
                  >
                    <compliance.Icon className="h-3 w-3" aria-hidden="true" />
                    {compliance.label}
                  </Badge>
                )}
                {corroborated && (
                  <Badge
                    variant="outline"
                    className={`ml-auto inline-flex items-center gap-1 text-[10px] ${meta.cls}`}
                  >
                    <Icon className="h-3 w-3" aria-hidden="true" />
                    {meta.label}
                  </Badge>
                )}
              </div>

              {breached && (
                <p className="rounded border border-red-500/30 bg-red-500/10 p-2 text-red-800 dark:text-red-300">
                  {law.breach?.trim() ||
                    'The agent flagged this contract as non-compliant but did not say which provision fails.'}
                </p>
              )}

              {law.relevance && <p className="text-muted-foreground">{law.relevance}</p>}

              {law.source_clause && (
                <p className="border-l-2 pl-2 italic text-muted-foreground">
                  &ldquo;{law.source_clause}&rdquo;
                </p>
              )}

              {corroborated && law.status === 'contradicted' && law.matrix_instrument && (
                <p className="text-amber-700 dark:text-amber-300">
                  Reference table gives <strong>{law.matrix_instrument}</strong> for this
                  jurisdiction. Check which applies before relying on either.
                </p>
              )}

              {typeof law.confidence === 'number' && (
                <p className="text-[10px] text-muted-foreground">
                  Model confidence {law.confidence}%
                </p>
              )}
            </li>
          );
        })}
      </ul>

      <p className="text-[10px] text-muted-foreground">
        Identified by the compliance agent from the contract text. Verify any citation before
        relying on it.{' '}
        {corroborated
          ? 'The gate verdict itself uses the reference table only — a law flagged here as breached does not reject the contract, but it does route approval to a senior reviewer.'
          : 'These are cross-checked against the reference table at the final gate, where the comparison is shown.'}
      </p>
    </div>
  );
}
